import { supabase } from '../lib/supabase';
import { API_CONFIG } from '../config/api';
import type {
  AIScenario,
  CreateScenarioRequest,
  UpdateScenarioRequest
} from '../types/ai-scenario';

export class AIScenarioService {
  /**
   * 获取可用的AI场景列表
   */
  static async getAvailableScenarios(): Promise<AIScenario[]> {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch scenarios');

      const result = await response.json();
      if (result.success) {
        return result.data.data || [];
      } else {
        throw new Error(result.error || 'Fetch failed');
      }
    } catch (error) {
      console.error('获取场景列表失败:', error);
      throw new Error(`获取场景列表失败: ${(error as Error).message}`);
    }
  }

  /**
   * 根据场景编码获取场景详情
   */
  static async getScenarioByCode(scenarioCode: string): Promise<AIScenario | null> {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=get&scenario_code=${scenarioCode}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null; // 未找到记录
        }
        throw new Error('Failed to fetch scenario');
      }

      const result = await response.json();
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Fetch failed');
      }
    } catch (error) {
      console.error('获取场景详情失败:', error);
      if ((error as Error).message.includes('404')) {
        return null;
      }
      throw new Error(`获取场景详情失败: ${(error as Error).message}`);
    }
  }

  /**
   * 处理提示词模板，替换变量
   */
  static processPromptTemplate(template: string, inputData: Record<string, any>): string {
    let processedPrompt = template;
    
    // 替换输入数据中的变量
    for (const [key, value] of Object.entries(inputData)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processedPrompt = processedPrompt.replace(regex, String(value));
    }
    
    // 处理带默认值的变量，如 {{max_length:200}}
    const defaultValueRegex = /{{(\w+):([^}]+)}}/g;
    processedPrompt = processedPrompt.replace(defaultValueRegex, (_, key, defaultValue) => {
      return inputData[key] !== undefined ? String(inputData[key]) : defaultValue;
    });
    
    return processedPrompt;
  }

  /**
   * 创建新场景（管理员功能）
   */
  static async createScenario(request: CreateScenarioRequest): Promise<AIScenario> {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) throw new Error('Failed to create scenario');

      const result = await response.json();
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Create failed');
      }
    } catch (error) {
      console.error('创建场景失败:', error);
      throw new Error(`创建场景失败: ${(error as Error).message}`);
    }
  }

  /**
   * 更新场景（管理员功能）
   */
  static async updateScenario(request: UpdateScenarioRequest): Promise<AIScenario> {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) throw new Error('Failed to update scenario');

      const result = await response.json();
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (error) {
      console.error('更新场景失败:', error);
      throw new Error(`更新场景失败: ${(error as Error).message}`);
    }
  }

  /**
   * 删除场景（管理员功能）
   */
  static async deleteScenario(id: number): Promise<void> {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete scenario');

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Delete failed');
      }
    } catch (error) {
      console.error('删除场景失败:', error);
      throw new Error(`删除场景失败: ${(error as Error).message}`);
    }
  }


}